import { RefreshCw, Stethoscope, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type { PatientBasic } from '@/components/medic/patientCardData';

interface Props {
    patient: PatientBasic;
    busy: boolean;
    /** Карточка открыта врачом, который указан лечащим. */
    isMine?: boolean;
    onReload: () => void;
    onAssignDoctor: () => void;
}

function valueOrDash(value: string): string {
    return value || '—';
}

export function PatientCardHeader({ patient, busy, isMine, onReload, onAssignDoctor }: Props) {
    const name = patient.name || 'Имя не указано';
    const birth = formatValue(patient.birthDate, '', 'date') || '—';
    const attendingIsMe = !!isMine && !!patient.attendingDoctorId;
    const attending = attendingIsMe
        ? 'Вы'
        : patient.attendingDoctorName || (patient.attendingDoctorId ? 'врач недоступен' : '');
    return (
        <header className="shrink-0 border-b bg-card/40 px-3 py-3 sm:px-4">
            <div className="flex flex-wrap items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-primary/10 text-primary sm:h-11 sm:w-11">
                    <UserRound className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 basis-[220px]">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="break-words text-base font-semibold">{name}</div>
                        {patient.code && (
                            <span className="rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
                                Карта № {patient.code}
                            </span>
                        )}
                        {attendingIsMe && (
                            <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
                                Мой пациент
                            </span>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>Дата рождения: <span className="text-foreground">{birth}</span></span>
                        <span>Пол: <span className="text-foreground">{valueOrDash(patient.genderLabel)}</span></span>
                        <span>Телефон: <span className="text-foreground">{valueOrDash(patient.phone)}</span></span>
                        <span className="inline-flex items-center gap-1">
                            <Stethoscope className="h-3 w-3" />
                            Лечащий врач:{' '}
                            <span className={attending ? 'text-foreground' : 'text-muted-foreground'}>
                                {attending || 'не назначен'}
                            </span>
                        </span>
                    </div>
                </div>
                <div className="flex w-full items-center justify-end gap-1.5 sm:w-auto">
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-[11px]"
                        onClick={onAssignDoctor}
                        disabled={busy}
                        title={patient.attendingDoctorId ? 'Передать пациента другому врачу' : 'Назначить лечащего врача'}
                    >
                        <Stethoscope className="mr-1 h-3 w-3" />
                        {patient.attendingDoctorId ? 'Передать' : 'Назначить'}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-[11px]"
                        onClick={onReload}
                        disabled={busy}
                        title="Обновить карточку"
                    >
                        <RefreshCw className={`mr-1 h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
                        Обновить
                    </Button>
                </div>
            </div>
        </header>
    );
}
